import {useEffect, useRef, useState} from 'react';
import {Form, Button} from '@goproblems/ui';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPaperPlane, faTimes} from '@fortawesome/free-solid-svg-icons';

import {
    useFetchTournamentChatMessagesQuery,
    usePostTournamentChatMessageMutation,
} from '@api';
import {TournamentChatMessage} from '@types';
import {TournamentChatContentType} from '@types';
import {MERCURE_PUBLIC_URL} from '@utils';
import {EventSource} from 'eventsource';
import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';
import {Loading} from '@components/Loading';
import {UserItem} from '@components/UserItem';

const UserMessage = ({chatMessage}: {chatMessage: TournamentChatMessage}) => (
    <div className="tournament-chat-message">
        <div className="tournament-chat-message-heading">
            <UserItem user={chatMessage.sender} showLevel={false} />
            <time dateTime={chatMessage.createdAt}>
                {dayjs(chatMessage.createdAt).format('HH:mm')}
            </time>
        </div>
        <p>{chatMessage.content}</p>
    </div>
);

const SystemMessage = ({chatMessage}: {chatMessage: TournamentChatMessage}) => {
    const {t} = useTranslation();

    const translationKey =
        chatMessage.contentType === TournamentChatContentType.SYSTEM &&
        chatMessage.content
            ? `tournaments.messages.${chatMessage.content}`
            : null;

    return (
        <div className="tournament-chat-system">
            {translationKey && t(translationKey, chatMessage.parameters)}
            <span className="ml-2 text-xs">
                {dayjs(chatMessage.createdAt).format('HH:mm')}
            </span>
        </div>
    );
};

export const TournamentChatBox = ({
    tournamentId,
    className,
    style,
    onClosed,
}: {
    tournamentId?: number | string;
    className?: string;
    style?: React.CSSProperties;
    onClosed?: () => void;
}) => {
    const {t} = useTranslation();
    const [messages, setMessages] = useState<TournamentChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [postTournamentChatMessage] = usePostTournamentChatMessageMutation();
    const [disabled, setDisabled] = useState(false);
    const {
        data: chatMessages,
        isLoading,
        refetch,
    } = useFetchTournamentChatMessagesQuery(tournamentId, {
        skip: !tournamentId,
        refetchOnMountOrArgChange: true,
    });

    const messagesRef = useRef<HTMLDivElement | null>(null);

    const sendMessage = async () => {
        if (!input.trim() || !tournamentId || disabled) return;
        setDisabled(true);

        try {
            await postTournamentChatMessage({
                tournamentId,
                body: {message: input},
            }).unwrap();
            setInput('');
        } catch (error) {
            console.error('Failed to send message:', error);
        } finally {
            setDisabled(false);
        }
    };

    useEffect(() => {
        if (!chatMessages) return;
        setMessages(chatMessages);
    }, [chatMessages]);

    useEffect(() => {
        if (!tournamentId) return;
        const topic = `/notifications/tournaments/${tournamentId}/chat`;
        const url = new URL(MERCURE_PUBLIC_URL, window.location.origin);
        url.searchParams.set('match', topic);
        const es = new EventSource(url, {
            withCredentials: true,
        });

        es.onopen = () => {
            console.log('✅ Connection to Mercure opened successfully.');
            void refetch();
        };

        es.onmessage = event => {
            console.log('🔔 Update received:', event.data);
            const mercureNotification = JSON.parse(event.data);
            const message =
                mercureNotification.payload?.message || mercureNotification;
            setMessages(prevMessages => [...prevMessages, message]);
        };

        es.onerror = error => {
            console.error('❌ EventSource error:', error);
        };

        return () => {
            es.close();
            console.log('❌ Connection to Mercure closed.');
        };
    }, [tournamentId, refetch]);

    useEffect(() => {
        if (messagesRef.current) {
            messagesRef.current.scrollTo({
                behavior: 'smooth',
                top: messagesRef.current.scrollHeight,
            });
        }
    }, [messages]);

    return (
        <div className={`tournament-chat ${className ?? ''}`} style={style}>
            <div className="tournament-chat-heading">
                <h3>{t('tournaments.chat')}</h3>
                <button
                    type="button"
                    aria-label={t('tournaments.close_chat')}
                    onClick={() => onClosed && onClosed()}
                    className="tournament-icon-button">
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <>
                <div className="tournament-chat-messages" ref={messagesRef}>
                    {isLoading && <Loading className="mt-3" />}
                    {!isLoading &&
                        messages.map(message => {
                            if (
                                message.contentType ===
                                TournamentChatContentType.SYSTEM
                            )
                                return (
                                    <SystemMessage
                                        key={message.id || message.createdAt}
                                        chatMessage={message}
                                    />
                                );
                            if (
                                message.contentType ===
                                TournamentChatContentType.TEXT
                            )
                                return (
                                    <UserMessage
                                        key={message.id || message.createdAt}
                                        chatMessage={message}
                                    />
                                );
                            return null;
                        })}
                </div>
                <div className="tournament-chat-compose">
                    <Form.Control
                        className="tournament-chat-input"
                        placeholder={t('tournaments.message_placeholder')}
                        aria-label={t('tournaments.message_placeholder')}
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={e =>
                            e.key === 'Enter' &&
                            !e.nativeEvent.isComposing &&
                            sendMessage()
                        }
                    />
                    <Button
                        onClick={sendMessage}
                        className="tournament-chat-send"
                        aria-label={t('tournaments.send_message')}
                        variant="primary"
                        disabled={disabled || !input.trim()}>
                        <FontAwesomeIcon icon={faPaperPlane} />
                    </Button>
                </div>
            </>
        </div>
    );
};
